import {Injectable} from '@angular/core';

@Injectable({
  providedIn: 'root' // This makes it available globally
})

export class ExplorerCommonFunctions {

  uniqueCompanyTypes(allFilterOptions: any): any[] {
    return Array.from(
      new Set(allFilterOptions.company_types)
    ).sort(); // Alphabetical order
  }

  uniqueBusinessTypes(allFilterOptions: any): any[] {
    return Array.from(
      new Set(allFilterOptions.business_types)
    ).sort(); // Alphabetical order
  }

  uniqueSupplierCategories = (() => {
    let prevSelectedSuppliers: any[] = []; // Store previous state internally

    return function (
      allFilterOptions: any,
      selectedSupplierCategories: any,
      selectedSuppliers: any
    ): any[] {
      const allCategories = [...allFilterOptions.supplier_categories]; // Get all available categories

      // Find newly added suppliers (suppliers that were not in previous selection)
      const newlyAddedSuppliers = selectedSuppliers.filter(
        (supplier: any) => !prevSelectedSuppliers.some((prev: any) => prev.id === supplier.id)
      );

      // Auto-add categories from only newly added suppliers
      newlyAddedSuppliers.forEach((supplier: any) => {
        supplier.categories.forEach((categoryName: string) => {
          const category = allCategories.find(cat => cat.name === categoryName);
          // Only add if it's not already in selectedSupplierCategories
          if (category && !selectedSupplierCategories.some((selCat: any) => selCat.name === category.name)) {
            selectedSupplierCategories.push(category);
          }
        });
      });

      // Update stored suppliers for the next call (without modifying selectedSupplierCategories)
      prevSelectedSuppliers = [...selectedSuppliers];

      return allCategories.sort((a, b) => a.name.localeCompare(b.name)); // Keep categories sorted
    };
  })();
  
  uniqueSuppliers(allFilterOptions: any, selectedSupplierCategories: any): any[] {
    return allFilterOptions.suppliers
      .filter((supplier: any) =>
        // If categories are selected, filter suppliers by categories.
        selectedSupplierCategories.length > 0
          ? supplier.categories.some((category: any) =>
            selectedSupplierCategories.some((selectedCat: any) => selectedCat.name === category)
          )
          : true // If no categories are selected, include all suppliers.
      )
      .slice() // Create a shallow copy
      .sort((a: any, b: any) => {
        // Sort by category first if selected categories are present, otherwise by supplier name
        const catA = a.categories.find((cat: any) => selectedSupplierCategories.some((sc: any) => sc.name === cat)) || '';
        const catB = b.categories.find((cat: any) => selectedSupplierCategories.some((sc: any) => sc.name === cat)) || '';
        return catA.localeCompare(catB) || a.name.localeCompare(b.name);
      });
  }

  uniqueProducts(allFilterOptions: any, selectedSupplierCategories: any, selectedSuppliers: any): any[] {
    return allFilterOptions.products
      .filter((product: any) =>
        // If categories and suppliers are selected, filter by both.
        selectedSupplierCategories.length > 0
          ? selectedSupplierCategories.some((category: any) => category.name === product.category) &&
          selectedSuppliers.some((supplier: any) => supplier.name === product.supplier)
          : selectedSuppliers.length > 0 // If no categories are selected, only filter by suppliers.
            ? selectedSuppliers.some((supplier: any) => supplier.name === product.supplier)
            : true // If no categories or suppliers are selected, return all products.
      )
      .slice() // Create a shallow copy
      .sort((a: any, b: any) =>
        a.supplier.localeCompare(b.supplier) ||
        a.category.localeCompare(b.category) ||
        a.name.localeCompare(b.name)
      );
  }

  uniqueSectors(allFilterOptions: any): any[] {
    return allFilterOptions.sectors
      .slice() // Create a shallow copy to avoid mutating the original array
      .sort((a: any, b: any) => a.name.localeCompare(b.name));
  }

  uniqueClusters(allFilterOptions: any, selectedRankings: any): any[] {

    if (selectedRankings.length > 0) {
      // Get the slugs from selectedRankings

      const uniqueClusters = allFilterOptions.clusters.filter((cluster: any) =>
        cluster.slugs.some((slug: string) =>
          allFilterOptions.rankings.some((ranking: any) => slug.endsWith(ranking.slug))
        )
      );

      // Sort clusters by name alphabetically and numerically if needed
      return uniqueClusters.sort((a: any, b: any) => {
        // Compare alphabetically, ignoring numbers
        const alphaA = a.name.replace(/\d+/g, '').trim();
        const alphaB = b.name.replace(/\d+/g, '').trim();
        const alphaCompare = alphaA.localeCompare(alphaB);

        if (alphaCompare !== 0) {
          return alphaCompare; // If different alphabetically, return the comparison result
        }

        // If names are alphabetically equal, sort by the numeric part (if any)
        const numA = parseInt(a.name.match(/\d+/)?.[0] || '0', 10);
        const numB = parseInt(b.name.match(/\d+/)?.[0] || '0', 10);

        return numA - numB; // Sort numerically within the same alphabetic group
      });
    } else {

      // Sort alphabetically, then numerically within the same alphabetic group
      return allFilterOptions.clusters.slice().sort((a: any, b: any) => {
        // First, compare alphabetically ignoring numbers
        const alphaA = a.name.replace(/\d+/g, '').trim();
        const alphaB = b.name.replace(/\d+/g, '').trim();
        const alphaCompare = alphaA.localeCompare(alphaB);

        if (alphaCompare !== 0) {
          return alphaCompare; // If different alphabetically, return the comparison result
        }

        // If names are alphabetically equal, sort by the numeric part (if any)
        const numA = parseInt(a.name.match(/\d+/)?.[0] || '0', 10);
        const numB = parseInt(b.name.match(/\d+/)?.[0] || '0', 10);

        return numA - numB; // Sort numerically within the same alphabetic group
      });
    }
  }

  uniqueLists(allFilterOptions: any): any[] {
    return allFilterOptions.rankings
      .slice() // Create a shallow copy to avoid mutating the original array
      .sort((a: any, b: any) => a.name.localeCompare(b.name));
  }

  uniqueSignificantMarkets(allFilterOptions: any): any[] {
    return allFilterOptions.significant_markets
      .slice() // Create a shallow copy to avoid mutating the original array
      .sort((a: any, b: any) => a.name.localeCompare(b.name));
  }

  uniqueRegions(allFilterOptions: any): any[] {
    return allFilterOptions.ranking_regions
      .slice() // Create a shallow copy to avoid mutating the original array
      .sort((a: any, b: any) => a.name.localeCompare(b.name));
  }

  uniqueHQs(allFilterOptions: any): any[] {
    return allFilterOptions.headquarters
      .slice() // Create a shallow copy to avoid mutating the original array
      .sort((a: any, b: any) => a.name.localeCompare(b.name));
  }


  getAllUniqueFilters(allFilterOptions: any, selectedSupplierCategories: any, selectedSuppliers: any, selectedRankings: any): any {
    return {
      companyTypes: this.uniqueCompanyTypes(allFilterOptions),
      businessTypes: this.uniqueBusinessTypes(allFilterOptions),
      supplierCategories: this.uniqueSupplierCategories(allFilterOptions, selectedSupplierCategories, selectedSuppliers),
      suppliers: this.uniqueSuppliers(allFilterOptions, selectedSupplierCategories),
      products: this.uniqueProducts(allFilterOptions, selectedSupplierCategories, selectedSuppliers),
      sectors: this.uniqueSectors(allFilterOptions),
      clusters: this.uniqueClusters(allFilterOptions, selectedRankings),
      rankings: this.uniqueLists(allFilterOptions),
      significantMarkets: this.uniqueSignificantMarkets(allFilterOptions),
      regions: this.uniqueRegions(allFilterOptions),
      headquarters: this.uniqueHQs(allFilterOptions)
    };
  }


  transformToDesiredFormat(idTransformFilters: any) {
    const transformedArray = [
      {
        name: "sector",
        values: idTransformFilters.sector.map((item: any) => item.id.toString())  // Assuming each item has an 'id'
      },
      {
        name: "headquarter",
        values: idTransformFilters.hq.map((item: any) => item.id.toString())  // Assuming each item has an 'id'
      },
      {
        name: "significant_market",
        values: idTransformFilters.sigMarket.map((item: any) => item.id.toString())  // Assuming each item has an 'id'
      },
      {
        name: "list",
        values: idTransformFilters.ranking.map((item: any) => item.id.toString())  // Assuming each item has an 'id'
      },
      {
        name: "cluster",
        values: idTransformFilters.cluster.map((item: any) => item.name.toString())  // Assuming each item has an 'id'
      },
      {
        name: "list_region",
        values: idTransformFilters.region.map((item: any) => item.id ? item.id.toString() : item.toString())
      },
      {
        name: "company_type",
        values: idTransformFilters.companyType.map((item: any) => item.toString())
      },
      {
        name: "business_type",
        values: idTransformFilters.businessType.map((item: any) => item.toString())
      },
      {
        name: "supplier_category",
        values: idTransformFilters.supplierCategory.map((item: any) => item.id.toString())
      },
      {
        name: "supplier",
        values: idTransformFilters.supplier.map((item: any) => item.id.toString())
      },
      {
        name: "product",
        values: idTransformFilters.product.map((item: any) => item.id.toString())
      }

    ];

    return transformedArray;
  }


}
